/**
 * LunarSip · Herb Library
 * 客户端分页：cards 全部预渲染，按 perPage 切片显示，底部数字页码导航 ← 1 2 3 4 5 →
 */
class LsHerbLibrary {
  constructor(root) {
    if (root._lsHerbLibraryInited) return;
    root._lsHerbLibraryInited = true;

    this.root = root;
    this.grid = root.querySelector('[data-grid]');
    this.pagination = root.querySelector('[data-pagination]');
    this.shownEl = root.querySelector('[data-shown]');
    this.cards = Array.from(this.grid?.querySelectorAll('.ls-herb-card') ?? []);
    this.perPage = Math.max(1, parseInt(root.dataset.perPage, 10) || 8);
    this.total = this.cards.length;
    this.totalPages = Math.max(1, Math.ceil(this.total / this.perPage));
    this.current = 1;

    if (!this.cards.length) return;
    this.render();
  }

  render() {
    const start = (this.current - 1) * this.perPage;
    const end = Math.min(start + this.perPage, this.total);

    this.cards.forEach((card, i) => {
      const visible = i >= start && i < end;
      card.toggleAttribute('hidden', !visible);
    });

    if (this.shownEl) {
      this.shownEl.textContent = this.total === 0 ? '0' : `${start + 1}–${end}`;
    }

    this.renderPagination();
  }

  renderPagination() {
    if (!this.pagination) return;
    this.pagination.innerHTML = '';
    if (this.totalPages <= 1) return;

    this.pagination.appendChild(
      this.makeBtn('←', {
        nav: true,
        disabled: this.current === 1,
        ariaLabel: 'Previous page',
        onClick: () => this.goTo(this.current - 1),
      })
    );

    for (const item of this.pageItems()) {
      if (item === '…') {
        const span = document.createElement('span');
        span.className = 'ls-herb-library__page-ellipsis';
        span.textContent = '…';
        this.pagination.appendChild(span);
        continue;
      }
      this.pagination.appendChild(
        this.makeBtn(String(item), {
          active: item === this.current,
          ariaLabel: `Page ${item}`,
          ariaCurrent: item === this.current ? 'page' : null,
          onClick: () => this.goTo(item),
        })
      );
    }

    this.pagination.appendChild(
      this.makeBtn('→', {
        nav: true,
        disabled: this.current === this.totalPages,
        ariaLabel: 'Next page',
        onClick: () => this.goTo(this.current + 1),
      })
    );
  }

  /**
   * 计算要展示的页码序列。
   * 总页数 ≤7 全列；否则保留首/末/当前±1，中间用 … 占位。
   */
  pageItems() {
    const total = this.totalPages;
    const cur = this.current;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const set = new Set([1, total, cur, cur - 1, cur + 1]);
    const arr = [...set].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < arr.length; i++) {
      result.push(arr[i]);
      if (i < arr.length - 1 && arr[i + 1] - arr[i] > 1) result.push('…');
    }
    return result;
  }

  makeBtn(label, { active, disabled, nav, onClick, ariaLabel, ariaCurrent } = {}) {
    const btn = document.createElement('button');
    btn.type = 'button';
    let cls = 'ls-herb-library__page-btn';
    if (active) cls += ' is-active';
    if (nav) cls += ' ls-herb-library__page-nav';
    btn.className = cls;
    btn.textContent = label;
    if (disabled) btn.disabled = true;
    if (ariaLabel) btn.setAttribute('aria-label', ariaLabel);
    if (ariaCurrent) btn.setAttribute('aria-current', ariaCurrent);
    if (onClick) btn.addEventListener('click', onClick);
    return btn;
  }

  goTo(page) {
    if (page < 1 || page > this.totalPages || page === this.current) return;
    this.current = page;
    this.render();
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function initLsHerbLibraries(root = document) {
  root.querySelectorAll('.ls-herb-library').forEach((el) => new LsHerbLibrary(el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initLsHerbLibraries());
} else {
  initLsHerbLibraries();
}

document.addEventListener('shopify:section:load', (event) => initLsHerbLibraries(event.target));
document.addEventListener('shopify:block:load', (event) => initLsHerbLibraries(event.target));

window.LsHerbLibrary = LsHerbLibrary;
window.initLsHerbLibraries = initLsHerbLibraries;
